import type { ReactElement, ReactNode } from 'react';
import { isValidElement, Suspense, useRef, useState } from 'react';
import { useLocation, useMatches } from 'react-router';
import { enableNewNavigation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useTextOverflow } from '@gonfalon/dom/useTextOverflow';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { msg } from '@gonfalon/messages/msg';
import { isMessageRef } from '@gonfalon/messages/ref';
import { matchProjectSettingsFullScreenFlow } from '@gonfalon/navigator/matchers';
import { toHref, toUnifiedSettings } from '@gonfalon/navigator/navigators';
import {
  Breadcrumb as Breadcrumb_,
  Breadcrumbs as Breadcrumbs_,
  IconButton,
  Link,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  Tooltip,
  TooltipTrigger,
  useFocus,
  useFocusVisible,
  useHover,
} from '@launchpad-ui/components';
import clsx from 'clsx';

import { ProjectBreadcrumbSwitcher, ProjectSwitcherSkeleton } from '../../ProjectSwitcher';
import { useSidebarContext } from '../../Sidebar';
import { isFlattenedSettingsEnabled } from '../SettingsNavigation/isFlattenedSettingsEnabled';
import { trackSettingsBreadcrumbClicked } from '../SettingsNavigation/settingsAnalytics';
import { useIsFlatProjectSettingsSurface } from '../SettingsNavigation/useIsFlatProjectSettingsSurface';
import { settingsTourAnchor, settingsTourSteps } from '../SettingsTour/settingsTourAnchors';

import { breadcrumbSchema, type BreadcrumbValue, flatSettingsBreadcrumbSchema } from './breadcrumbSchemas';
import { useShowsProjectSwitcherCrumb } from './useShowsProjectSwitcherCrumb';

import styles from './Breadcrumbs.module.css';

export type BreadcrumbMenuItem = {
  id: string;
  text: string;
  href?: string;
  icon?: ReactNode;
  onPress?: () => void;
};

type Item = {
  // id is used by react-aria-components under the hood
  id: string;
  text?: string | ReactNode;
  href: string;
  icon?: ReactNode;
  onPress?: () => void;
  menu?: BreadcrumbMenuItem[];
};

export type BreadcrumbTrailItem = {
  id: string;
  text: string;
  href?: string;
  icon?: ReactNode;
  onPress?: () => void;
  menu?: BreadcrumbMenuItem[];
};

export function Breadcrumbs({ trail }: { trail?: BreadcrumbTrailItem[] } = {}) {
  const matches = useMatches();
  const location = useLocation();
  const sidebar = useSidebarContext();

  const isFlatSettings = isFlattenedSettingsEnabled() && !matchProjectSettingsFullScreenFlow(location);
  const isFlatProjectSettingsSurface = useIsFlatProjectSettingsSurface();
  const showsProjectSwitcher = useShowsProjectSwitcherCrumb();
  const flatOverrides = isFlatSettings
    ? matches.flatMap((match) => {
        const parsed = flatSettingsBreadcrumbSchema.safeParse(match.handle);
        return parsed.success ? [parsed.data.flatSettings] : [];
      })
    : [];
  const leadingOverride = flatOverrides.findLast((override) => override.leading)?.leading;
  const isOrganizationSettings = flatOverrides.some((override) => override.scope === 'organization');

  const crumbs = matches.reduce((acc, match) => {
    const override = isFlatSettings ? flatSettingsBreadcrumbSchema.safeParse(match.handle) : undefined;
    const parse = breadcrumbSchema.safeParse(match.handle);

    let breadcrumb: BreadcrumbValue;
    if (override?.success) {
      breadcrumb = override.data.flatSettings.breadcrumb;
    } else if (parse.success) {
      breadcrumb = parse.data.breadcrumb;
    } else {
      return acc;
    }

    let text;

    if (isMessageRef(breadcrumb)) {
      text = msg(breadcrumb.messageId);
    } else if (typeof breadcrumb === 'function') {
      if (!match.loaderData) {
        return acc;
      }
      text = breadcrumb({ data: match.loaderData });
    } else {
      text = breadcrumb;
    }

    if (text === null || text === undefined || text === '') {
      return acc;
    }

    acc.push({
      id: match.id,
      text,
      href: match.pathname,
    });
    return acc;
  }, new Array<Item>());

  let leading: ReactElement[] | undefined;
  if (isFlatSettings) {
    if (leadingOverride) {
      leading = leadingOverride;
    } else if (isFlatProjectSettingsSurface) {
      leading = showsProjectSwitcher
        ? [
            <Suspense key="project" fallback={<ProjectSwitcherSkeleton />}>
              <span {...settingsTourAnchor(settingsTourSteps.switchProjects)}>
                <ProjectBreadcrumbSwitcher />
              </span>
            </Suspense>,
          ]
        : [];
    } else if (isOrganizationSettings) {
      leading = [];
    }
  }

  const currentHref = `${location.pathname}${location.search}`;
  const items = trail
    ? trail.map((item) => ({ ...item, href: item.href ?? currentHref }))
    : leading
      ? [
          {
            id: 'flat-settings',
            text: msg('chrome.settingsNavigation.title'),
            href: toHref(toUnifiedSettings()),
            onPress: () => trackSettingsBreadcrumbClicked({ crumb: 'settings', fromPath: location.pathname }),
          },
          ...leading.map((element, index) => ({ id: `flat-settings-leading-${index}`, text: element, href: '' })),
          ...crumbs,
        ]
      : crumbs;

  return (
    <QuietErrorBoundary severity="medium">
      <Breadcrumbs_
        items={items}
        className={clsx(styles.breadcrumbs, {
          [styles.withSidebar]: sidebar.isVisible,
          [styles.newNavigation]: enableNewNavigation(),
        })}
      >
        {(item) => <Breadcrumb {...item} />}
      </Breadcrumbs_>
    </QuietErrorBoundary>
  );
}

const MAX_CHARS = 64;

function Breadcrumb(item: Item) {
  if (item.menu) {
    return <CollapsedBreadcrumb label={typeof item.text === 'string' ? item.text : ''} menu={item.menu} />;
  }
  return <TextBreadcrumb {...item} />;
}

function CollapsedBreadcrumb({ label, menu }: { label: string; menu: BreadcrumbMenuItem[] }) {
  return (
    <Breadcrumb_ className={clsx(styles.crumb, styles.shrink, styles.collapsed)}>
      <MenuTrigger>
        <IconButton variant="minimal" size="small" icon="more-horiz" aria-label={label} />
        <Popover placement="bottom start">
          <Menu aria-label={label} items={menu}>
            {(entry) => (
              <MenuItem id={entry.id} href={entry.href} textValue={entry.text} onAction={entry.onPress}>
                {entry.icon}
                {entry.text}
              </MenuItem>
            )}
          </Menu>
        </Popover>
      </MenuTrigger>
    </Breadcrumb_>
  );
}

function TextBreadcrumb(item: Item) {
  const { href, text, icon, onPress } = item;

  const innerRef = useRef<HTMLSpanElement>(null);
  const { ref, hasOverflow, props: overflowProps } = useTextOverflow<HTMLSpanElement>();
  const { hoverProps, isHovered } = useHover({});
  const [isFocused, setFocused] = useState(false);
  const { focusProps } = useFocus({
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  });
  const { isFocusVisible } = useFocusVisible();

  if (isValidElement(text)) {
    return <Breadcrumb_ className={clsx(styles.crumb, styles.shrink)}>{text}</Breadcrumb_>;
  }

  return (
    <Breadcrumb_
      className={clsx(styles.crumb, {
        [styles.shrink]: typeof text === 'string' && text.length < MAX_CHARS,
      })}
    >
      <TooltipTrigger isDisabled={!hasOverflow} isOpen={hasOverflow && (isHovered || (isFocusVisible && isFocused))}>
        {/* @ts-expect-error react-aria-components and react-aria are in disagreement about the event type here, but it's legit. */}
        <Link href={href} onPress={onPress} {...overflowProps} {...focusProps} ref={ref}>
          <span ref={innerRef} {...hoverProps}>
            {icon && <span className={styles.icon}>{icon}</span>}
            {text}
          </span>
        </Link>
        <Tooltip className={styles.itemText} triggerRef={ref} placement="bottom start">
          {text}
        </Tooltip>
      </TooltipTrigger>
    </Breadcrumb_>
  );
}
